import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Phone, Menu, X, ArrowRight, UserCircle2 } from "lucide-react";
import { track } from "../../lib/track";

const LOGO_SRC = "/assets/pgps-logo.jpg";

const LINKS = [
  { to: "/procurement", label: "Procurement" },
  { to: "/industrial-mro", label: "Industrial & MRO" },
  { to: "/federal-dla", label: "Federal & DLA" },
  { to: "/prime-contractors", label: "Prime Contractors" },
  { to: "/capability-statement", label: "Capability Statement" },
  { to: "/logistics", label: "Logistics" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function SiteNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_2px_20px_-12px_rgba(11,31,58,.35)]" : "bg-white/90 backdrop-blur-sm"
      }`}
    >
      <div className="pg-container flex items-center justify-between gap-6 h-[76px]">
        <Link data-testid="logo-home-link" to="/" className="flex items-center gap-3 flex-shrink-0" aria-label="Patrick Global Home">
          <div className="h-12 w-[68px] overflow-hidden flex items-center justify-center">
            <img src={LOGO_SRC} alt="Patrick Global logo" className="h-[150%] w-auto max-w-none object-contain" />
          </div>
          <div className="hidden sm:block xl:hidden leading-tight text-left">
            <div className="font-[Poppins] font-bold text-[15px] text-[var(--pg-navy)] tracking-tight whitespace-nowrap">PATRICK GLOBAL</div>
            <div className="text-[8.5px] tracking-[0.14em] text-[var(--pg-accent)] uppercase font-semibold whitespace-nowrap">We Source. We Procure. We Deliver.</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-3">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `font-[Poppins] font-medium text-[12px] whitespace-nowrap transition-colors ${
                  isActive ? "text-[var(--pg-accent)]" : "text-[var(--pg-navy)] hover:text-[var(--pg-accent)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
          <a data-testid="nav-call-link" href="tel:+12819019914" onClick={() => track("phone_click", { source: "nav" })} className="hidden items-center gap-1.5 text-[12.5px] font-[Poppins] font-medium text-[var(--pg-navy)] hover:text-[var(--pg-accent)] transition-colors whitespace-nowrap">
            <Phone size={14} /> 281-901-9914
          </a>
          <Link data-testid="nav-portal-link" to="/portal" className="flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold text-[var(--pg-navy)] hover:text-[var(--pg-accent)] transition-colors whitespace-nowrap">
            <UserCircle2 size={15} /> Client Portal
          </Link>
          <button data-testid="nav-request-quote-button" onClick={() => navigate("/request-a-quote")} className="btn-primary !py-2.5 !px-4 text-[12.5px] whitespace-nowrap">
            Submit an RFQ <ArrowRight size={14} />
          </button>
        </div>

        <button data-testid="mobile-menu-toggle" aria-label="Toggle menu" className="xl:hidden p-2 rounded-md hover:bg-slate-100" onClick={() => setOpen(!open)}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-white border-t border-slate-200 max-h-[calc(100vh-76px)] overflow-y-auto">
          <div className="pg-container py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                onClick={() => setOpen(false)}
                className="text-left py-3 px-2 font-[Poppins] font-medium text-[15px] text-[var(--pg-navy)] border-b border-slate-100"
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to="/portal"
              data-testid="mobile-nav-portal-link"
              onClick={() => setOpen(false)}
              className="text-left py-3 px-2 font-[Poppins] font-semibold text-[15px] text-[var(--pg-accent)] border-b border-slate-100"
            >
              Client Portal
            </NavLink>
            <button
              data-testid="mobile-request-quote-button"
              onClick={() => { setOpen(false); navigate("/request-a-quote"); }}
              className="btn-primary mt-3 justify-center"
            >
              Submit an RFQ <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
